/**
 * Resume upload.
 *
 * The extracted text is shown in an editable box, not hidden behind a
 * "processing complete" checkmark. Extraction gets things wrong — columns
 * interleave, headers repeat, ligatures mangle — and the user is the only one
 * who can tell. Confirming is a separate act from uploading for the same
 * reason it is on the answers screen: nothing goes out under someone's name
 * that they have not seen in the form we hold it.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { getAuthClient, ApiError, api } from "./auth";
import { NextStep, type NextStepInfo } from "./NextStep";
import { ErrorList } from "./fields";

interface Resume {
  id: string;
  label: string;
  text: string;
  kind: "pdf" | "docx" | "text" | null;
  pages: number | null;
  bytes: number | null;
  confirmedAt: string | null;
  createdAt: string;
}

const ACCEPT = ".pdf,.docx,.txt,.md";

/** One resume on the shelf (C5): several kept, one in use. */
interface ResumeSummary {
  id: string;
  label: string;
  kind: "pdf" | "docx" | "text" | null;
  pages: number | null;
  bytes: number | null;
  words: number;
  confirmedAt: string | null;
  createdAt: string;
  active: boolean;
}

/**
 * The shelf: every resume the person keeps, with the one in use marked.
 * Matching, the drafts and the extension all read the one in use; a new
 * upload becomes it; switching is one click. Removing takes the file and
 * the text away for good, so it asks twice.
 */
function Shelf({ items, busy, onActivate, onRename, onRemove, onUpload }: {
  items: ResumeSummary[];
  busy: boolean;
  onActivate: (id: string) => void;
  onRename: (id: string, label: string) => void;
  onRemove: (id: string) => void;
  onUpload: () => void;
}) {
  const [renaming, setRenaming] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  if (items.length === 0) return null;
  return (
    <div className="shelf">
      <div className="people-head">
        <span className="field-label">{items.length === 1 ? "Your resume" : `Your resumes · ${items.length}`}</span>
        <button className="step-link" onClick={onUpload} disabled={busy}>Upload another</button>
      </div>
      <p className="muted small">The one in use is what matching scores against, what drafts are written from, and what the extension attaches. A new upload becomes it.</p>
      <ul className="shelf-list">
        {items.map((r) => (
          <li key={r.id} className={r.active ? "active" : ""}>
            <label className="shelf-pick">
              <input type="radio" name="active-resume" checked={r.active} disabled={busy} onChange={() => onActivate(r.id)} />
              <span className="cap">{r.active ? "In use" : "Use this"}</span>
            </label>
            <div className="shelf-body">
              {renaming === r.id ? (
                <span className="shelf-rename">
                  <input className="input" value={draft} disabled={busy} onChange={(e) => setDraft(e.target.value)}
                         onKeyDown={(e) => { if (e.key === "Enter") { onRename(r.id, draft); setRenaming(null); } if (e.key === "Escape") setRenaming(null); }} />
                  <button className="btn small" disabled={busy || !draft.trim()} onClick={() => { onRename(r.id, draft); setRenaming(null); }}>Save</button>
                  <button className="btn ghost small" disabled={busy} onClick={() => setRenaming(null)}>Cancel</button>
                </span>
              ) : (
                <span className="shelf-name">{r.label}</span>
              )}
              <span className="muted small">
                {[r.kind?.toUpperCase(), r.pages ? `${r.pages} page${r.pages > 1 ? "s" : ""}` : null, `${r.words.toLocaleString()} words`, r.confirmedAt ? "confirmed" : "not yet confirmed"].filter(Boolean).join(" · ")}
              </span>
            </div>
            <span className="shelf-actions">
              {renaming !== r.id && <button className="step-link small" disabled={busy} onClick={() => { setRenaming(r.id); setDraft(r.label); }}>Rename</button>}
              {confirmRemove === r.id ? (
                <>
                  <button className="step-link small err" disabled={busy} onClick={() => { onRemove(r.id); setConfirmRemove(null); }}>Remove for good</button>
                  <button className="step-link small" disabled={busy} onClick={() => setConfirmRemove(null)}>Keep</button>
                </>
              ) : (
                <button className="step-link small" disabled={busy} onClick={() => setConfirmRemove(r.id)}>Remove</button>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function describeFile(r: Resume): string {
  const parts: string[] = [];
  if (r.kind) parts.push(r.kind.toUpperCase());
  if (r.pages) parts.push(`${r.pages} page${r.pages > 1 ? "s" : ""}`);
  if (r.bytes) parts.push(`${Math.max(1, Math.round(r.bytes / 1024))} KB`);
  return parts.join(" · ");
}

export default function ResumeScreen({ onSaved, onBack, next, onGo }: { onSaved: () => void; onBack: () => void; next: NextStepInfo; onGo: (go: string) => void }) {
  const [resume, setResume] = useState<Resume | null>(null);
  const [text, setText] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<null | "upload" | "confirm" | "shelf">(null);
  const [shelf, setShelf] = useState<ResumeSummary[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    setLoading(true);
    setErrors([]);
    Promise.all([api<{ resume: Resume | null }>("/api/resume"), api<{ resumes: ResumeSummary[] }>("/api/resumes")])
      .then(([r, list]) => { setResume(r.resume); setText(r.resume?.text ?? ""); setShelf(list.resumes); })
      .catch((e) => setErrors(e instanceof ApiError ? e.messages : [(e as Error).message]))
      .finally(() => setLoading(false));
  }, []);

  /** A change to the shelf: the list comes back with the call; the text follows the one in use. */
  const shelfCall = (path: string, init: RequestInit) => {
    setBusy("shelf");
    setErrors([]);
    api<{ resumes: ResumeSummary[] }>(path, init)
      .then((list) => { setShelf(list.resumes); return api<{ resume: Resume | null }>("/api/resume"); })
      .then((r) => { setResume(r.resume); setText(r.resume?.text ?? ""); onSaved(); })
      .catch((e) => setErrors(e instanceof ApiError ? e.messages : [(e as Error).message]))
      .finally(() => setBusy(null));
  };

  useEffect(load, [load]);

  /**
   * Not using api() here: it sets content-type to application/json, and a
   * multipart body needs the browser to generate its own boundary. Overriding
   * the header by hand is the usual way this breaks.
   */
  const upload = async (file: File) => {
    setBusy("upload");
    setErrors([]);
    try {
      const client = await getAuthClient();
      const token = await client.getTokenSilently();
      const body = new FormData();
      body.append("file", file);

      const res = await fetch("/api/resume", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body,
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErrors((payload as { errors?: string[] }).errors ?? [`Upload failed (${res.status}).`]);
        return;
      }
      const r = (payload as { resume: Resume }).resume;
      setResume(r);
      setText(r.text);
      api<{ resumes: ResumeSummary[] }>("/api/resumes").then((list) => setShelf(list.resumes)).catch(() => {});
      onSaved();
    } catch (e) {
      setErrors([(e as Error).message]);
    } finally {
      setBusy(null);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const confirm = async () => {
    if (!resume) return;
    setBusy("confirm");
    setErrors([]);
    try {
      const r = await api<{ resume: Resume }>("/api/resume/confirm", {
        method: "POST",
        body: JSON.stringify({ id: resume.id, text }),
      });
      setResume(r.resume);
      setText(r.resume.text);
      setShelf((list) => list.map((x) => (x.id === r.resume.id ? { ...x, confirmedAt: r.resume.confirmedAt, words: r.resume.text.trim().split(/\s+/).filter(Boolean).length } : x)));
      onSaved();
    } catch (e) {
      setErrors(e instanceof ApiError ? e.messages : [(e as Error).message]);
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <p className="muted">Loading…</p>;

  const dirty = resume !== null && text.trim() !== resume.text.trim();

  return (
    <>
      <header className="bar">
        <div>
          <h1 className="tight">Your resume</h1>
          <p className="muted small">We read the text out of it. You get to correct it.</p>
        </div>
        <button className="btn ghost small" onClick={onBack}>Back</button>
      </header>

      <input
        ref={fileInput} type="file" accept={ACCEPT} className="hidden-input"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }}
      />

      {shelf.length === 0 && (
        <div className="row">
          <button className="btn" onClick={() => fileInput.current?.click()} disabled={busy !== null}>
            {busy === "upload" ? "Reading…" : "Choose a file"}
          </button>
        </div>
      )}
      <p className="muted small">PDF, Word (.docx), or plain text. Up to 8 MB.</p>

      <Shelf
        items={shelf}
        busy={busy !== null}
        onUpload={() => fileInput.current?.click()}
        onActivate={(id) => shelfCall(`/api/resumes/${id}/active`, { method: "PUT" })}
        onRename={(id, label) => shelfCall(`/api/resumes/${id}`, { method: "PUT", body: JSON.stringify({ label }) })}
        onRemove={(id) => shelfCall(`/api/resumes/${id}`, { method: "DELETE" })}
      />
      {!resume && shelf.length > 0 && (
        <p className="notice small">
          This track has no resume yet. Pick one above to use it here, or upload a new one —
          whatever you choose becomes this track's resume, and your other tracks keep theirs.
        </p>
      )}
      {busy === "upload" && <p className="muted small">Reading…</p>}

      <ErrorList errors={errors} />

      {resume && (
        <>
          <h2>What we read{shelf.length > 1 ? ` — ${resume.label}` : ""}</h2>
          {resume.confirmedAt && !dirty ? (
            <p className="badge">Confirmed</p>
          ) : (
            <p className="notice small">
              Read this through before confirming. Extraction gets columns and
              headers wrong sometimes, and this text is what we match jobs
              against — fix anything that came out garbled.
            </p>
          )}

          <textarea
            className="input resume-text"
            value={text}
            spellCheck={false}
            onChange={(e) => setText(e.target.value)}
            aria-label="Extracted resume text"
          />
          <p className="muted small">
            {text.trim().split(/\s+/).filter(Boolean).length.toLocaleString()} words
          </p>

          <div className="row">
            <button className="btn" onClick={confirm}
                    disabled={busy !== null || (Boolean(resume.confirmedAt) && !dirty)}>
              {busy === "confirm" ? "Saving…"
                : resume.confirmedAt && dirty ? "Save and re-confirm"
                : resume.confirmedAt ? "Confirmed"
                : "This looks right"}
            </button>
          </div>
        </>
      )}

      <NextStep ready={!!resume?.confirmedAt && !dirty} next={next} onGo={onGo} onBack={onBack} doneWords="Resume confirmed" />
    </>
  );
}
